
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'

// types
import { RPPT } from '@/core/types/rpp.types'

// realisations
import { rpp } from '@/core/realisations/rpp.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// composable
import { useRoute } from 'vue-router'
import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'
import { useRound } from '@/common/composable/useRound'

import { formatDateWithTimeFromBackend, formatDateFromBackend } from '@/common/helpers/formatDate'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        Cheap,
        Loader,
        Modal,
        TheTextarea,
    },
    setup() {
        const account = useAccount()
        const notifications = useNotifications()
        const { roundTo } = useRound()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            rpp: null as RPPT | null,
            is_head_of_transport_department: false,
            is_accountant: false,
            comment: null as string | null,
            statusCode: null as string | null,
            is_submit: false,
            is_submit_loading: false,
        })

        const data_state = reactive({
            rpp: new DataState<RPPT>({}),
        })

        const route = useRoute()
        const rpp_id = Number(route.params.id)

        const loadRPP = async () => {
            try {
                state.is_loading = true
                state.is_failed = false
                const response = await rpp.getRPP(rpp_id)

                state.rpp = response.data
            } catch (error) {
                console.log(error)
                state.is_failed = true
            } finally {
                state.is_loading = false
                state.is_failed = false
            }
        }

        onBeforeMount(async () => {
            state.is_head_of_transport_department = account.haveRole(22)
            state.is_accountant = account.haveRole(23)

            await loadRPP()
        })

        const changeStatusTo = async (status_code: string) => {
            try {
                if (!state.rpp) return

                await rpp.changeStatusRPP(rpp_id, status_code)

                notifications.notify({
                    title: 'Статус изменен',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При изменении статуса произошла ошибка!',
                })
            }
        }

        const handleChangeStatus = async (status_code: string) => {
            const answer = confirm(`Вы подтверждаете смену статуса?`)
            if (!answer) return

            await changeStatusTo(status_code)
            await loadRPP()
        }

        const toggleChangeStatus = (statusCode?: string) => {
            state.comment = null
            state.is_submit = !state.is_submit
            state.statusCode = statusCode || null
        }

        const getFullPath = () => {
            if (!state.rpp) return

            let stocks = [...state.rpp.stock_delivery_notes]

            let full_path = stocks
                .sort(
                    (stock_one, stock_two) => stock_one.receiver_number - stock_two.receiver_number
                )
                .map((stock) => stock.city_name)
                .join(' - ')
            full_path = state.rpp.sender_city_name + ' - ' + full_path

            return full_path || null
        }

        const isOnInspectionHeadTransportStatus = computed(() => {
            // 'На проверке у РТО'
            return state.rpp && state.rpp.status_id === 11
        })

        const isOnApprovedStatus = computed(() => {
            // 'Подтвержден'
            return state.rpp && state.rpp.status_id === 13
        })

        const isOnEditStatus = computed(() => {
            // 'Подтвержден' или 'К оплате'
            return state.rpp && (state.rpp.status_id === 13 || state.rpp.status_id === 15)
        })
        const getCostPriceTitle = computed(() =>
            state.rpp && state.rpp.with_nds ? 'Себестоимость (с НДС):' : 'Себестоимость (без НДС):'
        )

        const submitRpp = async () => {
            if (!state.rpp || !state.rpp.id || !state.statusCode) return

            try {
                state.is_submit_loading = true

                const payload = {
                    statusCode: state.statusCode,
                    comment: state.comment || '',
                    paymentBySupplierIds: [state.rpp.id],
                }

                await rpp.submitRpp(payload)

                let notifyStr = 'подтвержден'
                if (state.statusCode === 'denied') {
                    notifyStr = 'отказан'
                }
                if (state.statusCode === 'completed') {
                    notifyStr = 'завершен'
                }
                notifications.notify({
                    title: `РПП успешно ${notifyStr}!`,
                    type: 'success',
                })
                toggleChangeStatus()
                await loadRPP()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                state.is_submit_loading = false
            }
        }

        return {
            state,
            data_state,
            rpp_id,
            loadRPP,
            //computed
            isOnInspectionHeadTransportStatus,
            isOnApprovedStatus,
            isOnEditStatus,
            toggleChangeStatus,
            handleChangeStatus,
            getFullPath,
            roundTo,
            getCostPriceTitle,
            submitRpp,
            formatDateWithTimeFromBackend,
            formatDateFromBackend,
        }
    },
})
